import { isKnownShapeKind, isRenderablePinShape, normalizeShape, type PinShape, type Shape, type SnapshotShape } from './shapes';

/** Shared pin dimensions for rendering, hit testing, and editor overlays. */
export const PIN_RADIUS = 14;
export const PIN_CENTER_Y_OFFSET = 16;
export const PIN_SELECTION_PADDING = 3;

export interface PinVisualBounds {
  left: number;
  top: number;
  width: number;
  height: number;
}

/**
 * Bounds of the selected pin, including its focus ring and pointed tail.
 * Keeping this in core prevents the web editor overlay from drifting away
 * from the renderer used by extension capture and raster flattening.
 */
export function pinVisualBounds(
  pin: Pick<PinShape, 'x' | 'y'>,
  padding = PIN_SELECTION_PADDING,
): PinVisualBounds {
  const radius = PIN_RADIUS + Math.max(0, padding);
  const top = pin.y - PIN_CENTER_Y_OFFSET - radius;
  const bottom = pin.y + Math.max(0, padding);
  return {
    left: pin.x - radius,
    top,
    width: radius * 2,
    height: bottom - top,
  };
}

/** Keep a moved pin fully visible inside a natural-coordinate canvas. */
export function clampPinTip(
  x: number,
  y: number,
  width: number,
  height: number,
): Pick<PinShape, 'x' | 'y'> {
  const radius = PIN_RADIUS + PIN_SELECTION_PADDING;
  return {
    x: clamp(x, radius, Math.max(radius, width - radius)),
    y: clamp(
      y,
      PIN_CENTER_Y_OFFSET + radius,
      Math.max(PIN_CENTER_Y_OFFSET + radius, height - PIN_SELECTION_PADDING),
    ),
  };
}

export function clamp(v: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, v));
}

/**
 * Point-in-shape test in natural (unzoomed) image coordinates.
 *
 * Tolerances are calibrated for pointer selection, not mathematical
 * containment: strokes hit within `width + 6px` of any sampled point,
 * circles hit on a band around the ellipse outline (not the interior,
 * so a circle drawn around something doesn't block clicks on shapes
 * inside it), and arrows hit within an 8px-padded bounding box.
 */
export function shapeHit(s: Shape, px: number, py: number): boolean {
  const normalized = normalizeShape(s as SnapshotShape);
  if (!isKnownShapeKind(normalized)) return false;
  s = normalized;
  if (s.kind === 'pin') {
    if (!isRenderablePinShape(s) || ![px, py].every(Number.isFinite)) return false;
    // x/y is the precise click point at the tip of the marker. The visible
    // badge sits above it and includes a small padded target for touch.
    const centerY = s.y - PIN_CENTER_Y_OFFSET;
    return Math.hypot(s.x - px, centerY - py) <= PIN_RADIUS + 5
      || (Math.abs(s.x - px) <= 6 && py >= centerY && py <= s.y + 3);
  }
  if (s.kind === 'stroke') {
    const tol = s.width + 6;
    for (const [x, y] of s.points) {
      if (Math.hypot(x - px, y - py) < tol) return true;
    }
    return false;
  }
  if (s.kind === 'circle') {
    const cx = s.x + s.w / 2;
    const cy = s.y + s.h / 2;
    const rx = Math.max(Math.abs(s.w / 2), 1);
    const ry = Math.max(Math.abs(s.h / 2), 1);
    const dx = px - cx, dy = py - cy;
    const d = (dx * dx) / (rx * rx) + (dy * dy) / (ry * ry);
    return Math.abs(d - 1) < 0.35;
  }
  if (s.kind === 'redact' || s.kind === 'blur' || s.kind === 'textBox') {
    const x1 = Math.min(s.x, s.x + s.w), x2 = Math.max(s.x, s.x + s.w);
    const y1 = Math.min(s.y, s.y + s.h), y2 = Math.max(s.y, s.y + s.h);
    return px >= x1 && px <= x2 && py >= y1 && py <= y2;
  }
  if (s.kind === 'arrow') {
    const x1 = Math.min(s.x1, s.x2) - 8, x2 = Math.max(s.x1, s.x2) + 8;
    const y1 = Math.min(s.y1, s.y2) - 8, y2 = Math.max(s.y1, s.y2) + 8;
    return px >= x1 && px <= x2 && py >= y1 && py <= y2;
  }
  return false;
}
